import { ethers } from 'ethers';
import ZenAbi from '../abi/ZenCapital.json';
import Erc20Abi from '../abi/ERC20.json';
import { CONTRACTS } from '../config/addresses';

export function zenContract(signerOrProvider) {
  return new ethers.Contract(CONTRACTS.zenCapital, ZenAbi, signerOrProvider);
}

export function usdtContract(signerOrProvider) {
  return new ethers.Contract(CONTRACTS.usdt, Erc20Abi, signerOrProvider);
}

// ---------- reads ----------
export async function fetchUser(provider, wallet) {
  const c = zenContract(provider);
  const u = await c.getUserByWallet(wallet);
  return {
    wallet: u.wallet,
    id: u.id.toString(),
    referrerId: u.referrerId.toString(),
    accountNumber: u.accountNumber.toString(),
    directReferrals: Number(u.directReferrals),
    pendingRewards: u.pendingRewards,
    totalEarned: u.totalEarned,
    totalProfitReported: u.totalProfitReported,
    registrationTime: u.registrationTime.toString(),
    registered: u.registered,
    blacklisted: u.blacklisted,
  };
}

export async function fetchLevels(provider, userId) {
  const c = zenContract(provider);
  const [counts, elig, cfg] = await Promise.all([
    c.getLevelMemberCounts(userId),
    c.getEligibility(userId),
    c.getLevelConfig(),
  ]);
  return {
    memberCounts: counts.map((x) => Number(x)),
    eligibility: elig,
    percents: cfg.percents.map((x) => Number(x)),
    directReqs: cfg.directReqs.map((x) => Number(x)),
    memberReqs: cfg.memberReqs.map((x) => Number(x)),
  };
}

// Paginated member IDs at a level (0-based level index). 10 per page.
export async function fetchLevelMembers(provider, userId, level, offset, limit) {
  const c = zenContract(provider);
  const [ids, total] = await c.getLevelMembers(userId, level, offset, limit);
  return { ids: ids.map((x) => x.toString()), total: Number(total) };
}

// Resolve a single MT5 account number to its on-chain user ID ("0" if none).
export async function resolveAccount(provider, account) {
  const c = zenContract(provider);
  try {
    const id = await c.accountToUserId(account);
    return id ? id.toString() : '0';
  } catch (_) {
    return '0';
  }
}

// Resolve a list of MT5 account numbers to on-chain user IDs.
// Returns { account: userId } where userId is "0" if not registered.
export async function resolveAccounts(provider, accounts) {
  const c = zenContract(provider);
  const out = {};
  // run in small concurrent batches to be gentle on the RPC
  const CHUNK = 20;
  for (let i = 0; i < accounts.length; i += CHUNK) {
    const slice = accounts.slice(i, i + CHUNK);
    const ids = await Promise.all(slice.map((a) => c.accountToUserId(a).catch(() => null)));
    slice.forEach((a, j) => { out[a] = ids[j] ? ids[j].toString() : '0'; });
  }
  return out;
}

export async function fetchRoles(provider) {
  const c = zenContract(provider);
  const [owner, admin, defRef] = await Promise.all([c.owner(), c.admin(), c.defaultReferrerId()]);
  return { owner: owner.toLowerCase(), admin: admin.toLowerCase(), defaultReferrerId: defRef.toString() };
}

export async function fetchStats(provider) {
  const c = zenContract(provider);
  const s = await c.getContractStats();
  return { totalUsers: s._totalUsers.toString(), usdtBalance: s._usdtBalance, blacklisted: s._blacklistedCount.toString() };
}

// On-chain activity log (register / claim) — newest first.
export async function fetchActivities(provider, userId, max = 50) {
  const c = zenContract(provider);
  const [list] = await c.getActivities(userId, 0, max);
  return list.map((a) => ({
    timestamp: Number(a.timestamp),
    type: Number(a.actType), // 0 = register, 1 = claim
    amount: a.amount,
    ref: a.ref.toString(),
  })).reverse();
}

// Level-income credited to this user, read from past events.
export async function fetchIncomeEvents(provider, userId, fromBlock = 0) {
  const c = zenContract(provider);
  const filter = c.filters.LevelIncomeEarned(userId);
  const logs = await c.queryFilter(filter, fromBlock, 'latest');
  // attach block timestamps
  const out = [];
  for (const log of logs) {
    let ts = 0;
    try { const b = await log.getBlock(); ts = b.timestamp; } catch (_) {}
    out.push({
      timestamp: ts,
      fromUserId: log.args.fromUserId.toString(),
      level: Number(log.args.level),
      amount: log.args.amount,
    });
  }
  return out.reverse();
}

// This week's income (sum of level income credited since `sinceTs`) and
// this week's own profit reported (sum of distributions where this user earned
// from their own account — approximated by income events in the window).
// `sinceTs` = unix seconds for the start of the current week (Monday 00:00).
export async function fetchThisWeek(provider, userId, sinceTs, fromBlock = 0) {
  const events = await fetchIncomeEvents(provider, userId, fromBlock);
  let income = ethers.BigNumber.from(0);
  let count = 0;
  for (const e of events) {
    if (e.timestamp >= sinceTs) {
      income = income.add(e.amount);
      count++;
    }
  }
  return { income, count };
}

// ---------- writes ----------
export async function register(signer, referrerId, accountNumber) {
  const c = zenContract(signer);
  const tx = await c.register(referrerId || 0, accountNumber);
  return tx.wait();
}

// Admin registers a user on their behalf (links the provided wallet).
export async function adminRegister(signer, userWallet, referrerId, accountNumber) {
  const c = zenContract(signer);
  const tx = await c.adminRegister(userWallet, referrerId || 0, accountNumber);
  return tx.wait();
}

// Look up a wallet's user id (0 if not registered).
export async function idOfWallet(provider, wallet) {
  const c = zenContract(provider);
  try { const id = await c.addressToId(wallet); return id ? id.toString() : '0'; }
  catch (_) { return '0'; }
}

export async function claim(signer) {
  const c = zenContract(signer);
  const tx = await c.claim();
  return tx.wait();
}

export async function distributeBatch(signer, weekId, accounts, profits) {
  const c = zenContract(signer);
  const tx = await c.distributeWeek(weekId, accounts, profits);
  const receipt = await tx.wait();
  return receipt;
}

// Parse LevelIncomeEarned events out of a distribution receipt into line-items:
// { userId, fromUser, level, amount(BigNumber) }
export function parseIncomeFromReceipt(signer, receipt) {
  const c = zenContract(signer);
  const out = [];
  if (!receipt || !receipt.logs) return out;
  for (const lg of receipt.logs) {
    try {
      const parsed = c.interface.parseLog(lg);
      if (parsed && parsed.name === 'LevelIncomeEarned') {
        out.push({
          userId: parsed.args.userId.toString(),
          fromUser: parsed.args.fromUserId.toString(),
          level: Number(parsed.args.level),
          amount: parsed.args.amount, // BigNumber
        });
      }
    } catch (_) { /* not our event */ }
  }
  return out;
}

export async function fund(signer, amountUnits) {
  const usdt = usdtContract(signer);
  const owner = await signer.getAddress();
  const allowance = await usdt.allowance(owner, CONTRACTS.zenCapital);
  if (allowance.lt(amountUnits)) {
    const ap = await usdt.approve(CONTRACTS.zenCapital, amountUnits);
    await ap.wait();
  }
  const c = zenContract(signer);
  const tx = await c.fundContract(amountUnits);
  return tx.wait();
}

export async function setLevelConfig(signer, level, percentBps, directReq, memberReq) {
  const c = zenContract(signer);
  const tx = await c.setLevelConfig(level, percentBps, directReq, memberReq);
  return tx.wait();
}

export async function fetchWithdrawFee(provider) {
  const c = zenContract(provider);
  const bps = await c.withdrawFeeBps();
  return Number(bps);
}

export async function setWithdrawFee(signer, bps) {
  const c = zenContract(signer);
  const tx = await c.setWithdrawFee(bps);
  return tx.wait();
}

export async function editAccount(signer, userId, newAccount) {
  const c = zenContract(signer);
  const tx = await c.editAccountNumber(userId, newAccount);
  return tx.wait();
}

export async function editWallet(signer, userId, newWallet) {
  const c = zenContract(signer);
  const tx = await c.editWallet(userId, newWallet);
  return tx.wait();
}

export async function setBlacklist(signer, userId, status) {
  const c = zenContract(signer);
  const tx = await c.setBlacklist(userId, status);
  return tx.wait();
}

export async function emergencyWithdrawAll(signer) {
  const c = zenContract(signer);
  const tx = await c.emergencyWithdrawAll();
  return tx.wait();
}

export async function emergencyWithdraw(signer, amountUnits) {
  const c = zenContract(signer);
  const tx = await c.emergencyWithdraw(amountUnits);
  return tx.wait();
}
